import { useCallback, useEffect, useState, memo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LoadingButton from '@mui/lab/LoadingButton';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import PropertiesForm, {
  ACTIONS,
} from '@components/PropertiesForm/PropertiesForm';

import {
  getGatewayProperties,
  getGateways,
  selectGatewayProperties,
  selectGateways,
  updateGatewayMapping,
  updateGatewayProperties,
} from '../../store/payment-options-gateways';
import {
  getCurrencies,
  selectCurrencies,
} from '../../store/payment-options-currencies';
import {
  getPaymentMethods,
  selectMethodList,
} from '../../store/payment-options-methods';
import {
  getPropertyKeys,
  selectPropertyKeys,
} from '../../store/payment-options-property-keys';

const FORM_ID = 'propertiesForm';

function GatewayPropertiesFormModal({
  open,
  data,
  action = ACTIONS.ADD,
  closeDialog = () => {},
  submitSuccessCallback = () => {},
}) {
  const dispatch = useDispatch();

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filteredMethodList, setFilteredMethodList] = useState([]);
  const [initialValues, setInitialValues] = useState([]);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [openDialog, setOpenDialog] = useState({ show: false });

  const filteredProperties = useRef([]);

  const currencyList = useSelector(selectCurrencies);
  const gatewayList = useSelector(selectGateways);
  const methodList = useSelector(selectMethodList);
  const propertyList = useSelector(selectPropertyKeys);
  const gatewayProperties = useSelector(selectGatewayProperties);

  /**
   * Fetches dropdown values.
   */
  useEffect(() => {
    setIsFetchingData(true);

    if (action === ACTIONS.EDIT) {
      const merchant = data.merchants[0];
      const { methodId, gatewayId } = data.methods[0];
      const { id: currencyId } = currencyList.find(
        (currency) => currency.code === data.currencies[0],
      );

      const payload = {
        merchantId: merchant.merchantId,
        methodId,
        gatewayId,
        currencyId,
      };

      return Promise.all([
        dispatch(getGateways()),
        dispatch(getCurrencies()),
        dispatch(getPaymentMethods()),
        dispatch(getPropertyKeys()),
        dispatch(getGatewayProperties(payload)),
      ]).then(() => {
        setIsFetchingData(false);
      });
    }

    return Promise.all([
      dispatch(getGateways()),
      dispatch(getCurrencies()),
      dispatch(getPaymentMethods()),
      dispatch(getPropertyKeys()),
    ]).then(() => {
      setIsFetchingData(false);
    });
  }, [
    dispatch,
    action,
    data.merchants,
    data.methods,
    data.currencies,
    currencyList,
  ]);

  /**
   * ACTION: ADD
   * Removes methods already added to the merchant
   */
  useEffect(() => {
    if (action !== ACTIONS.ADD) return;

    const methodIds = data.methods.map((method) => method.methodId);
    const filteredMethods = methodList.filter(
      (method) => !methodIds.includes(method.methodId),
    );

    setFilteredMethodList(filteredMethods);
    filteredProperties.current = propertyList;

    setInitialValues({});
  }, [action, data.methods, gatewayList, methodList, propertyList]);

  /**
   * ACTION: EDIT
   * Preselects method, gateway and props
   */
  useEffect(() => {
    if (action !== ACTIONS.EDIT) return;

    setFilteredMethodList(data.methods);

    if (!gatewayProperties || gatewayProperties.length === 0) return;

    const propertyKeys = Object.keys(gatewayProperties);
    const selectedProperties = propertyList
      .filter((prop) => propertyKeys.includes(prop.key))
      .map((prop) => ({ ...prop, value: gatewayProperties[prop.key] }));

    filteredProperties.current = selectedProperties;

    const values = {
      gateways: gatewayList.find((gw) => gw.id === data.methods[0].gatewayId),
      methods: data.methods ? data.methods[0] : [],
      properties: gatewayProperties ? filteredProperties.current : [],
      propertyValues: gatewayProperties ? filteredProperties.current : [],
      methodList: filteredMethodList,
    };

    setInitialValues(values);
  }, [
    action,
    data.methods,
    filteredMethodList,
    gatewayList,
    gatewayProperties,
    methodList,
    propertyList,
  ]);

  const onGatewayChange = useCallback(
    (gateway) => {
      if (action === ACTIONS.ADD) return;
      const merchant = data.merchants[0];
      const method = data.methods[0];
      const { id: currencyId } = currencyList.find(
        (currency) => currency.code === data.currencies[0],
      );

      const payload = {
        merchantId: merchant.merchantId,
        methodId: method.methodId,
        gatewayId: gateway.id,
        currencyId,
      };

      dispatch(getGatewayProperties(payload));
    },
    [
      action,
      currencyList,
      data.currencies,
      data.merchants,
      data.methods,
      dispatch,
    ],
  );

  const onCancelClick = useCallback(() => {
    setFormSuccessMsg('');
    setFormErrorMsg('');
    closeDialog();
  }, [closeDialog]);

  // eslint-disable-next-line no-unused-vars
  const validateForm = useCallback((_values, _e) => {
    const errors = {};
    // TODO
    return errors;
  }, []);

  const onSubmit = useCallback(
    (values, e) => {
      setFormSuccessMsg('');
      setFormErrorMsg('');

      const errors = validateForm(values, e);

      if (Object.keys(errors).length > 0) return;

      const { id: currencyId } = currencyList.find(
        (currency) => currency.code === data.currencies[0],
      );

      const payload = {
        merchantIds: data.merchants.map((merchant) => merchant.merchantId),
        currencyIds: [currencyId],
        methodIds:
          action === ACTIONS.ADD
            ? values.methods.map((method) => method.methodId)
            : [data.methods[0].methodId],
        gatewayIds:
          action === ACTIONS.ADD
            ? [values.gateway.id]
            : [data.methods[0].gatewayId],

        properties: values.propertyValues?.reduce(
          (prev, prop) => ({
            ...prev,
            [prop.id.toString()]: prop.value,
          }),
          {},
        ),
      };

      setIsSubmitting(true);
      dispatch(updateGatewayProperties(payload)).then(
        ({ payload: body, error }) => {
          if (body && body.status === 'SUCCESS') {
            // edit + different gateway
            if (action === ACTIONS.EDIT) {
              const { gatewayId } = data.methods[0];
              if (gatewayId === values.gateways.id) {
                submitSuccessCallback();
                closeDialog(true);
                return;
              }
            }

            const mappingPayload = {
              merchantIds: payload.merchantIds,
              currencyIds: payload.currencyIds,
              methodIds: payload.methodIds,
              gatewayId: payload.gatewayIds[0],
            };

            dispatch(updateGatewayMapping(mappingPayload))
              .then(({ payload: resp, error: respErr }) => {
                if (resp && resp.status === 'SUCCESS') {
                  submitSuccessCallback();
                  closeDialog(true);
                } else {
                  setFormErrorMsg(respErr.message);
                }
              })
              .finally(() => {
                setIsSubmitting(false);
              });
          } else {
            setFormErrorMsg(error.message);
            setIsSubmitting(false);
          }
        },
      );
    },
    [
      validateForm,
      currencyList,
      data.merchants,
      data.methods,
      data.currencies,
      action,
      dispatch,
      submitSuccessCallback,
      closeDialog,
    ],
  );

  /**
   * Needed to submit final-form outside its context.
   */
  const onSubmitClick = useCallback(() => {
    // { cancelable: true } required for Firefox
    // https://github.com/facebook/react/issues/12639#issuecomment-382519193
    document
      .getElementById(FORM_ID)
      .dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }, []);

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md">
      <DialogTitle>
        {action === ACTIONS.EDIT
          ? `Editing ${data.lobName} method: ${data.methods[0].methodName} (${data.methods[0].methodCode})`
          : `Add new method to ${data.lobName}`}
      </DialogTitle>
      <DialogContent style={{ paddingTop: '12px', textAlign: 'center' }}>
        {isFetchingData ? (
          <CircularProgress />
        ) : (
          <PropertiesForm
            formId={FORM_ID}
            action={action}
            initialValues={initialValues}
            currencies={data.currencies}
            merchants={data.merchants}
            gateways={gatewayList}
            properties={propertyList}
            methods={filteredMethodList}
            handleOnFormSubmit={onSubmit}
            handleOnGatewayChange={onGatewayChange}
            validateForm={validateForm}
          />
        )}
        {formSuccessMsg && (
          <Alert
            onClose={() => setFormSuccessMsg('')}
            severity="success">
            {formSuccessMsg}
          </Alert>
        )}
        {formErrorMsg && (
          <Alert
            onClose={() => setFormErrorMsg('')}
            severity="error">
            {formErrorMsg}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isSubmitting}
          onClick={onCancelClick}>
          Cancel
        </Button>
        <ProdConfirmationDialog
          openDialog={openDialog}
          yesHandler={onSubmitClick}
        />
        <LoadingButton
          variant="contained"
          loading={isSubmitting}
          loadingPosition="start"
          startIcon={<AddIcon />}
          onClick={(e) =>
            process.env.REACT_APP_ENV !== 'production'
              ? onSubmitClick(e)
              : handlePreSubmit(e)
          }>
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
export default memo(GatewayPropertiesFormModal);
